import request from 'lib/request';
import type { BugsFeatureRow, NewBugsFeature } from 'pages/bugs-features/types';

/**
 * The validation-error envelope the bugs/features endpoints answer 422 with: an
 * array of coded errors, not the older field-keyed hash. Only the message is
 * shown; the code and details exist for callers that want to branch on them.
 */
export interface BugsFeatureErrorResponse {
  errors?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  }[];
}

/**
 * Submits a new report. Resolves to the created row, or throws with a message
 * suitable for display.
 *
 * Lives with the form rather than with the listing hook because the form is
 * mounted at the app shell — pulling the listing's fetch machinery into every
 * page just to submit would be backwards.
 *
 * The Jira ticket is created by a background job, so the returned row will have
 * syncState 'pending' and no key yet — the table shows that state explicitly
 * rather than pretending the ticket exists.
 *
 * @param {NewBugsFeature} report - The report being filed.
 * @param {string[]} attachmentSignedIds - Active Storage signed ids from the direct upload.
 * @return {Promise<BugsFeatureRow>} The created row, syncState 'pending'.
 */
export async function submitBugsFeature(
  report: NewBugsFeature,
  attachmentSignedIds: string[] = []
): Promise<BugsFeatureRow> {
  // post() requires an AbortController. Nothing cancels a submit — the user has
  // committed by this point — so it exists only to satisfy the signature.
  const controller = new AbortController();

  const response = await request.post<BugsFeatureRow, BugsFeatureErrorResponse>(
    '/feedback',
    {
      bugs_feature: {
        report_type: report.reportType,
        title: report.title,
        description: report.description,
        // Reassembled into markers server-side by Comments::Mentions.embed.
        mentions: (report.mentions ?? []).map((mention) => ({
          name: mention.name,
          user_id: mention.userId,
        })),
        severity: report.severity ?? null,
        // Signed ids from the direct upload, not file bodies — the files are
        // already in storage by this point.
        attachments: attachmentSignedIds,
        context: {
          page_url: window.location.href,
          // Only present when the form was opened from the error page. Sent as context
          // rather than folded into the description so it survives the reporter editing
          // their own words, and so it is one allowlisted key on the server rather than
          // free text to parse back out.
          error_message: report.crash?.message,
          error_stack: report.crash?.stack,
        },
      },
    },
    controller
  );

  if (response.type === 'success') return response.data;

  if (response.type === 'error') {
    throw new Error(response.data?.errors?.[0]?.message ?? 'Could not submit');
  }

  throw new Error('Could not submit');
}
